import { Box, CircularProgress, Link, Tabs, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InsurancePeriod } from '../../../backendsdk';
import { TabPanel, a11yProps } from '../../../components/Tab';
import { TrackedTab as Tab } from '../../../components/TrackedComponents';
import { useAlert } from '../../../hooks/alert';
import useApi from '../../../hooks/api';
import InsurancePeriods from './InsurancePeriods';
import UploadLossRuns from './UploadLossRuns';

const LossRunsComponent: React.FC = () => {
    const [tab, setTab] = useState<number>(0);
    const [periods, setPeriods] = useState<InsurancePeriod[]>([]);
    const [isPeriodModalOpen, setIsPeriodModalOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [alertElement, setAlert] = useAlert();
    const { agencyApi } = useApi();
    const { t } = useTranslation();

    const loadInsurancePeriods = () => {
        setIsLoading(true);
        setIsError(false);
        agencyApi
            .agencyV0PeriodGet()
            .then((res) => {
                setPeriods(res.data);
            })
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        loadInsurancePeriods();
    }, []);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    };

    if (isLoading || isError) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {isLoading ? (
                    <CircularProgress />
                ) : (
                    <>
                        <Typography variant="overline" sx={{ lineHeight: 1.5 }}>
                            {t('content.loss_runs.error_loading_periods')}
                        </Typography>
                        <Link component="button" onClick={() => loadInsurancePeriods()}>
                            <Typography variant="overline">{t('content.loss_runs.try_again')}</Typography>
                        </Link>
                    </>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {alertElement}
            <Tabs
                value={tab}
                onChange={handleTabChange}
                TabIndicatorProps={{ sx: { bgcolor: 'secondary.main', height: 3 } }}
            >
                <Tab
                    label={t('content.loss_runs.insurance_periods')}
                    {...a11yProps(0)}
                    id="insurance-periods-tab"
                    data-testid="tab-0"
                />
                <Tab
                    label={t('content.loss_runs.loss_runs')}
                    {...a11yProps(1)}
                    id="loss-runs-tab"
                    data-testid="tab-1"
                />
            </Tabs>
            <TabPanel value={tab} index={0} sx={{ height: '100%' }}>
                <InsurancePeriods
                    periods={periods}
                    isPeriodModalOpen={isPeriodModalOpen}
                    setIsPeriodModalOpen={setIsPeriodModalOpen}
                    setInsurancePeriods={setPeriods}
                    setAlert={setAlert}
                />
            </TabPanel>
            <TabPanel value={tab} index={1} sx={{ height: '100%' }}>
                <UploadLossRuns
                    periods={periods}
                    switchTab={() => {
                        setIsPeriodModalOpen(true);
                        setTab(0);
                    }}
                    setAlert={setAlert}
                />
            </TabPanel>
        </Box>
    );
};

export default LossRunsComponent;
